import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'

export interface SearchFilters {
  imLookingFor: string[]
  orientation: string[]
  iLiveIn: string[]
  distance: string[]
}
export interface ExtendedSearchFilters extends SearchFilters {
  gender: string[]
  location: string[]
  ageRange: string[]
  status: string[]
  sizeRange: string[]
  weightRange: string[]
  hairColor: string[]
  eyeColor: string[]
  intimateHairCut: string[]
  nationality: string[]
  ethnicity: string[]
  languages: string[]
  penisSize: string[]
  practices: number[]
  braCup: string[]
  tattoo: boolean | null
  smoker: boolean | null
  piercing: boolean | null
  massageOnly: boolean | null
  alcohol: boolean | null
  verifiedStatus: string | null
}

interface SearchFiltersContextProps {
  filters: SearchFilters
  setFilters: (filters: SearchFilters) => void
  updateFilter: (key: keyof SearchFilters, value: any) => void
}

const SearchFiltersContext = createContext<
  SearchFiltersContextProps | undefined
>(undefined)

export const SearchFiltersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [filters, setFilters] = useState<SearchFilters>(() => {
    try {
      // Tentative de récupération des filtres depuis le localStorage
      const savedFilters = localStorage.getItem('searchFilters')
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters)
        // Si la récupération est correcte, on renvoie les filtres parsés
        if (parsedFilters && typeof parsedFilters === 'object') {
          return {
            imLookingFor: parsedFilters.imLookingFor || [],
            orientation: parsedFilters.orientation || [],
            iLiveIn: parsedFilters.iLiveIn || [],
            distance: parsedFilters.distance || [],
          }
        }
      }
    } catch (error) {
      // Si une erreur survient lors de la récupération ou de la parse, on logue l'erreur sans faire échouer le processus
      console.warn('Failed to parse search filters from localStorage', error)
    }
    // Si pas de filtres dans le localStorage, on retourne un état par défaut
    return {
      imLookingFor: [],
      orientation: [],
      iLiveIn: [],
      distance: [],
    }
  })

  // Sauvegarder les filtres dans le localStorage
  useEffect(() => {
    try {
      localStorage.setItem('searchFilters', JSON.stringify(filters))
    } catch (error) {
      // Loguer l'erreur si elle se produit lors de l'enregistrement des filtres
      console.warn('Failed to save search filters to localStorage', error)
    }
  }, [filters])

  // Fonction pour mettre à jour un filtre spécifique
  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters(prevFilters => {
      const newFilters = {
        ...prevFilters,
        [key]: value,
      }

      return newFilters
    })
  }

  return (
    <SearchFiltersContext.Provider
      value={{ filters, setFilters, updateFilter }}
    >
      {children}
    </SearchFiltersContext.Provider>
  )
}

export const useSearchFilters = () => {
  const context = useContext(SearchFiltersContext)
  if (!context) {
    throw new Error(
      'useSearchFilters must be used within a SearchFiltersProvider',
    )
  }
  return context
}
